import {
  FlexBox,
  space,
  Table,
  theme,
  ToggleSwitch,
  Typography,
} from "@waldner-laboreinrichtungen/component-system";
import { useTranslation } from "react-i18next";
import styledComponents, { css } from "styled-components";

import type {
  DeviceWithAutomations,
  SupportedAutomations,
  WeekDays,
} from "../view-model";
import { WEEK_DAYS } from "../view-model";

export type OnSelectDayProps = {
  selectedDay: WeekDays;
  selectedDevice: DeviceWithAutomations;
  selectedAutomation: SupportedAutomations;
};

export type OnToggleAutomationProps = {
  selectedDevice: DeviceWithAutomations;
  selectedAutomation: SupportedAutomations;
  disabled: boolean;
};

type TimeControlProps = {
  devices: DeviceWithAutomations[];
  onSelectDay: (props: OnSelectDayProps) => void;
  onToggleAutomation: (props: OnToggleAutomationProps) => void;
};

const CELL_WIDTH = "110px";

const StyledCell = styledComponents.div`
  display: flex;
  width: ${CELL_WIDTH};
  padding: ${space(3)} ${space(4)};
  justify-content: center;
  align-items: center;
  border-radius: ${space(4)};
  border: 1px solid ${theme.colorPalette.grey200};
  cursor: pointer;
  ${({ color }) =>
    color &&
    css`
      color: ${color};
    `}
`;

export const TimeControl = ({
  devices,
  onSelectDay,
  onToggleAutomation,
}: TimeControlProps) => {
  const { t } = useTranslation();

  return (
    <Table>
      <Table.Head hasBorder={false}>
        <Table.Row padding={`${space(0)} ${space(4)}`}>
          <Table.Header />
          <Table.Header />
          {WEEK_DAYS.map((day) => (
            <Table.Header key={day}>{t(`timecontrol.${day}`)}</Table.Header>
          ))}
        </Table.Row>
      </Table.Head>
      <Table.Body>
        {devices.map((device, index) =>
          Object.entries(device.automations).map(([automation, value]) => (
            <Table.Row
              hasBorder={
                (automation as SupportedAutomations) === "operation" &&
                index !== devices.length - 1
              }
              key={device.id + automation}
              padding={`${
                (automation as SupportedAutomations) === "operation"
                  ? space(2)
                  : space(7)
              } ${space(2)} ${
                (automation as SupportedAutomations) === "light"
                  ? space(2)
                  : space(8)
              }  ${space(2)}`}
            >
              <Table.Data>
                {(automation as SupportedAutomations) === "light" && (
                  <>
                    <Typography block size="xs" weight="semiBold">
                      {device.name}
                    </Typography>
                    <Typography block color="grey400" size="xs">
                      {`${device?.location.position || ""}`}
                    </Typography>
                  </>
                )}
              </Table.Data>
              <Table.Data>
                <FlexBox paddingRight={space(4)}>
                  <ToggleSwitch
                    checked={value ? Boolean(value.enabled) : false}
                    onChange={(event) =>
                      onToggleAutomation({
                        selectedDevice: device,
                        selectedAutomation: automation as SupportedAutomations,
                        disabled: !event.target.checked,
                      })
                    }
                  >
                    <Typography nowrap size="xs">
                      {t(`timecontrol.${automation}`)}
                    </Typography>
                  </ToggleSwitch>
                </FlexBox>
              </Table.Data>
              {WEEK_DAYS.map((day) => (
                <Table.Data key={day}>
                  <StyledCell
                    color={
                      value.enabled
                        ? theme.colorPalette.grey600
                        : theme.colorPalette.grey200
                    }
                    onClick={() =>
                      onSelectDay({
                        selectedDevice: device,
                        selectedDay: day,
                        selectedAutomation: automation as SupportedAutomations,
                      })
                    }
                  >
                    {value[day]
                      ? `${value[day]?.startTime}-${value[day]?.endTime}`
                      : "-"}
                  </StyledCell>
                </Table.Data>
              ))}
            </Table.Row>
          ))
        )}
      </Table.Body>
    </Table>
  );
};
